import { useState, useEffect } from "react";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
import { BookOpen, GraduationCap, School, Menu, Calendar, AlertTriangle, Check, ClipboardList, Users, Clock, MousePointer2, Phone, X, Save } from "lucide-react";
const API_BASE = "https://schoolcrm-fzcv.onrender.com";

// ─── Data ────────────────────────────────────────────────────────────────────

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// ⚠  EXACT same IDs as TimetablePage.jsx — used ONLY as a fallback for a
// class that has no custom periods saved yet. Once Admin sets custom period
// times for a class in Timetable Management, THOSE times are used instead
// (fetched below from the same /api/timetables/:className the timetable
// page saves to), so Logbook always matches what Admin actually assigned.
const DEFAULT_PERIODS = [{
  id: 1,
  label: "Period 1",
  time: "8:30 – 9:10",
  durationMin: 40,
  isBreak: false
}, {
  id: 2,
  label: "Period 2",
  time: "9:10 – 9:50",
  durationMin: 40,
  isBreak: false
}, {
  id: 3,
  label: "Period 3",
  time: "9:50 – 10:30",
  durationMin: 40,
  isBreak: false
}, {
  id: 4,
  label: "Break",
  time: "10:30 – 10:45",
  durationMin: 15,
  isBreak: true
}, {
  id: 5,
  label: "Period 4",
  time: "10:45 – 11:25",
  durationMin: 40,
  isBreak: false
}, {
  id: 6,
  label: "Period 5",
  time: "11:25 – 12:05",
  durationMin: 40,
  isBreak: false
}, {
  id: 7,
  label: "Lunch",
  time: "12:05 – 12:45",
  durationMin: 40,
  isBreak: true
}, {
  id: 8,
  label: "Period 6",
  time: "12:45 – 1:25",
  durationMin: 40,
  isBreak: false
}, {
  id: 9,
  label: "Period 7",
  time: "1:25 – 2:05",
  durationMin: 40,
  isBreak: false
}, {
  id: 10,
  label: "Period 8",
  time: "2:05 – 2:45",
  durationMin: 40,
  isBreak: false
}];

// Converts the `periods` array as saved by TimetablePage.jsx (which uses
// `duration` as the field name) into the shape Logbook renders with
// (`durationMin`), so both pages read the exact same saved data without
// any field-name mismatch.
function normalizePeriods(rawPeriods) {
  if (!Array.isArray(rawPeriods) || rawPeriods.length === 0) return null;
  return rawPeriods.map(p => ({
    id: p.id,
    label: p.label,
    time: p.time,
    durationMin: p.durationMin ?? p.duration ?? 40,
    isBreak: !!p.isBreak
  }));
}

// ─── GROUP NAMES ──────────────────────────────────────────────────────────────
// These MUST match exactly what AdmissionPage saves into the DB (form.group value).
// AdmissionPage HS_GROUPS values: "Bio & Maths", "Computer Science", "Arts & Science", "Commerce"
const CLASSES = {
  "1": {
    sections: ["A", "B", "C"],
    groups: null
  },
  "2": {
    sections: ["A", "B", "C"],
    groups: null
  },
  "3": {
    sections: ["A", "B", "C"],
    groups: null
  },
  "4": {
    sections: ["A", "B", "C"],
    groups: null
  },
  "5": {
    sections: ["A", "B", "C"],
    groups: null
  },
  "6": {
    sections: ["A", "B", "C"],
    groups: null
  },
  "7": {
    sections: ["A", "B", "C"],
    groups: null
  },
  "8": {
    sections: ["A", "B", "C"],
    groups: null
  },
  "9": {
    sections: ["A", "B", "C"],
    groups: null
  },
  "10": {
    sections: ["A", "B", "C"],
    groups: null
  },
  "11": {
    sections: null,
    groups: {
      "Bio & Maths": ["A", "B"],
      "Computer Science": ["A", "B"],
      "Arts & Science": ["A"],
      "Commerce": ["A", "B"]
    }
  },
  "12": {
    sections: null,
    groups: {
      "Bio & Maths": ["A", "B"],
      "Computer Science": ["A", "B"],
      "Arts & Science": ["A"],
      "Commerce": ["A", "B"]
    }
  }
};

// ─── URL builders ─────────────────────────────────────────────────────────────

// Timetable fetch key — e.g. "Class 11 Bio & Maths-A"
function buildTimetableKey(cls, section, group) {
  if ((cls === "11" || cls === "12") && group) {
    return `Class ${cls} ${group}-${section}`;
  }
  return `Class ${cls}-${section}`;
}

// Admission fetch key — MUST include group for 11/12 so the backend
// by-class route can filter correctly.
// Backend splits on the LAST "-" to get section; everything before is the class+group.
// Format: "Class 11 Bio & Maths-A"
// Backend regex: /^Class\s+\d+/ → applyingClass="Class 11", rest="Bio & Maths", group="Bio & Maths"
function buildAdmissionKey(cls, section, group) {
  if ((cls === "11" || cls === "12") && group) {
    return `Class ${cls} ${group}-${section}`;
  }
  return `Class ${cls}-${section}`;
}
function getTodayName() {
  return new Date().toLocaleDateString("en-IN", {
    weekday: "long"
  });
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function LogBook() {
  const todayDisplay = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric"
  });
  const todayName = getTodayName();
  const defaultDay = DAYS.includes(todayName) ? todayName : "Monday";

  // ── State ──────────────────────────────────────────────────────────────────
  const [selectedClass, setSelectedClass] = useState("1");
  const [selectedSection, setSelectedSection] = useState("A");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activePeriodId, setActivePeriodId] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedDay, setSelectedDay] = useState(defaultDay);
  const [attendance, setAttendance] = useState({});

  // Real students from admission DB
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState(null);

  // Timetable from DB
  const [timetableData, setTimetableData] = useState(null);
  const [timetableLoading, setTimetableLoading] = useState(false);
  const [timetableError, setTimetableError] = useState(null);
  // ✅ Period times as actually saved for THIS class in Timetable Management.
  // Falls back to DEFAULT_PERIODS only if the class has never had custom
  // period times saved.
  const [classPeriods, setClassPeriods] = useState(DEFAULT_PERIODS);

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveMessageType, setSaveMessageType] = useState(null);
  const is11or12 = selectedClass === "11" || selectedClass === "12";

  // ── Mobile: default sidebar closed on small screens (mobile-only behavior) ──
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  }, []);

  // ── Fetch admitted students ──────────────────────────────────────────────
  // Key change: pass selectedGroup into buildAdmissionKey so Class 11/12
  // fetches are group-specific, not mixed.
  useEffect(() => {
    // For 11/12: only fetch once a group is actually selected
    if (is11or12 && !selectedGroup) return;
    const classKey = buildAdmissionKey(selectedClass, selectedSection, selectedGroup);
    setStudentsLoading(true);
    setStudentsError(null);
    setStudents([]);
    setAttendance({});
    setSaveMessage(null);
    fetch(`${API_BASE}/api/admissions/by-class/${encodeURIComponent(classKey)}`).then(async res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    }).then(data => {
      const mapped = data.map(s => ({
        id: s._id,
        rollNo: s.rollNo,
        name: s.studentName,
        parent: s.fatherName || "—",
        mobile: s.fatherMobile || ""
      }));
      setStudents(mapped);
    }).catch(err => {
      console.error("Students fetch error:", err.message);
      setStudentsError(err.message);
    }).finally(() => setStudentsLoading(false));
  }, [selectedClass, selectedSection, selectedGroup]);

  // ── Fetch timetable ──────────────────────────────────────────────────────
  useEffect(() => {
    if (is11or12 && !selectedGroup) return;
    const className = buildTimetableKey(selectedClass, selectedSection, selectedGroup);
    setTimetableLoading(true);
    setTimetableError(null);
    setTimetableData(null);
    setClassPeriods(DEFAULT_PERIODS); // reset to default while loading / until we know otherwise

    fetch(`${API_BASE}/api/timetables/${encodeURIComponent(className)}`).then(async res => {
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        throw new Error(`Expected JSON — check API_BASE="${API_BASE}" and CORS.`);
      }
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    }).then(doc => {
      if (!doc || !doc.timetable) {
        setTimetableData(null);
        setClassPeriods(DEFAULT_PERIODS);
        return;
      }
      const norm = {};
      Object.entries(doc.timetable).forEach(([day, periods]) => {
        norm[day] = {};
        Object.entries(periods).forEach(([pid, cell]) => {
          norm[day][Number(pid)] = cell;
        });
      });
      setTimetableData(norm);
      // ✅ Use the SAME period times Admin assigned for this class in
      // Timetable Management, instead of the hardcoded defaults.
      setClassPeriods(normalizePeriods(doc.periods) || DEFAULT_PERIODS);
    }).catch(err => {
      console.error("Timetable fetch error:", err.message);
      setTimetableError(err.message);
    }).finally(() => setTimetableLoading(false));
  }, [selectedClass, selectedSection, selectedGroup]);

  // ── Cell lookup ───────────────────────────────────────────────────────────
  function getCell(day, periodId) {
    if (!timetableData) return null;
    return timetableData?.[day]?.[periodId] || null;
  }
  const currentPeriodMeta = classPeriods.find(p => p.id === activePeriodId);
  const currentCell = getCell(selectedDay, activePeriodId);

  // ── Attendance helpers ────────────────────────────────────────────────────
  function mark(studentId, status) {
    setAttendance(prev => {
      const current = prev[activePeriodId]?.[studentId];
      const newStatus = current === status ? null : status;
      return {
        ...prev,
        [activePeriodId]: {
          ...(prev[activePeriodId] || {}),
          [studentId]: newStatus
        }
      };
    });
    if (saveMessage) setSaveMessage(null);
  }
  function getStatus(sid) {
    return attendance[activePeriodId]?.[sid] || null;
  }
  function markAll(status) {
    const updates = {};
    students.forEach(s => {
      updates[s.id] = status;
    });
    setAttendance(prev => ({
      ...prev,
      [activePeriodId]: updates
    }));
    if (saveMessage) setSaveMessage(null);
  }
  const presentCount = students.filter(s => getStatus(s.id) === "P").length;
  const absentCount = students.filter(s => getStatus(s.id) === "A").length;
  const lateCount = students.filter(s => getStatus(s.id) === "L").length;
  const markedCount = students.filter(s => getStatus(s.id) !== null).length;
  const completion = students.length ? Math.round(markedCount / students.length * 100) : 0;

  // ── Save Attendance ───────────────────────────────────────────────────────
  async function handleSave() {
    if (students.length === 0) {
      setSaveMessage("No students to mark attendance for");
      setSaveMessageType("error");
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }
    if (currentPeriodMeta?.isBreak) {
      setSaveMessage("Cannot save attendance for Break or Lunch periods");
      setSaveMessageType("error");
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }
    const entries = [];
    for (const student of students) {
      const status = getStatus(student.id);
      if (status) {
        entries.push({
          studentId: student.id,
          rollNo: student.rollNo,
          name: student.name,
          status
        });
      }
    }
    if (entries.length === 0) {
      setSaveMessage("No attendance marked for this period");
      setSaveMessageType("error");
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }
    let className = `Class ${selectedClass}`;
    if (is11or12 && selectedGroup) className = `Class ${selectedClass} ${selectedGroup}`;
    const payload = {
      className,
      section: selectedSection,
      group: is11or12 ? selectedGroup : null,
      day: selectedDay,
      periodId: activePeriodId,
      periodLabel: currentPeriodMeta?.label,
      timeRange: currentPeriodMeta?.time,
      durationMin: currentPeriodMeta?.durationMin,
      subject: currentCell?.subject || null,
      teacher: currentCell?.teacher || null,
      entries
    };
    setSaving(true);
    setSaveMessage(null);
    try {
      const response = await fetch(`${API_BASE}/api/attendance/regular/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSaveMessage(`Attendance saved for ${currentPeriodMeta?.label} (${entries.length} students)`);
        setSaveMessageType("success");
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        throw new Error(data.message || "Failed to save attendance");
      }
    } catch (error) {
      console.error("Save error:", error);
      setSaveMessage(`Error: ${error.message}`);
      setSaveMessageType("error");
      setTimeout(() => setSaveMessage(null), 4000);
    } finally {
      setSaving(false);
    }
  }

  // ── Class change ──────────────────────────────────────────────────────────
  function handleClassChange(cls) {
    setSelectedClass(cls);
    const info = CLASSES[cls];
    if (info.sections) {
      setSelectedSection("A");
      setSelectedGroup(null);
    } else {
      // Auto-select first group and its first section
      const firstGroup = Object.keys(info.groups)[0];
      setSelectedGroup(firstGroup);
      setSelectedSection(info.groups[firstGroup][0]);
    }
    setAttendance({});
    setActivePeriodId(1);
    setSaveMessage(null);
    // Auto-close sidebar after picking a class on mobile
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return <div className="lb-root" style={{
    fontFamily: "'Poppins', sans-serif",
    display: "flex",
    height: "100vh",
    background: "#E3F2FD",
    color: "#0D47A1",
    overflow: "hidden"
  }}>

      {/* ════════ SIDEBAR BACKDROP (mobile only) ════════ */}
      <div className={`lb-backdrop ${sidebarOpen ? "show" : ""}`} onClick={() => setSidebarOpen(false)} />

      {/* ════════ SIDEBAR ════════ */}
      <div className={`lb-sidebar ${sidebarOpen ? "open" : ""}`} style={{
      width: sidebarOpen ? 260 : 0,
      minWidth: sidebarOpen ? 260 : 0,
      background: "#FFFFFF",
      borderRight: "1px solid #90CAF9",
      display: "flex",
      flexDirection: "column",
      overflowY: "auto",
      overflowX: "hidden",
      transition: "all 0.25s ease",
      boxShadow: sidebarOpen ? "2px 0 12px rgba(13,71,161,0.08)" : "none",
      position: "relative",
      zIndex: 10
    }}>
        <div style={{
        padding: "24px 20px 16px",
        borderBottom: "1px solid #90CAF9"
      }}>
          <div style={{
          fontSize: 11,
          color: "#2196F3",
          letterSpacing: "1px",
          textTransform: "uppercase",
          marginBottom: 6
        }}>
            <BookOpen className="inline w-[1em] h-[1em] align-[-0.125em]" /> LogBook
          </div>
          <div style={{
          fontSize: 20,
          fontWeight: 700,
          color: "#0D47A1"
        }}>Class Directory</div>
        </div>

        {/* Classes 1–10 */}
        <div style={{
        padding: "16px 16px 8px"
      }}>
          <SideLabel><GraduationCap className="inline w-[1em] h-[1em] align-[-0.125em]" /> Classes 1 – 10</SideLabel>
          {Array.from({
          length: 10
        }, (_, i) => String(i + 1)).map(cls => {
          const active = selectedClass === cls && !is11or12;
          return <div key={cls}>
                <SideBtn label={`Class ${cls}`} badge={cls} active={selectedClass === cls} onClick={() => handleClassChange(cls)} />
                {selectedClass === cls && CLASSES[cls].sections && <div style={{
              display: "flex",
              gap: 6,
              paddingLeft: 40,
              paddingBottom: 8,
              flexWrap: "wrap"
            }}>
                    {CLASSES[cls].sections.map(sec => <SecBtn key={sec} sec={sec} active={selectedSection === sec} onClick={() => {
                setSelectedSection(sec);
                setAttendance({});
                setSaveMessage(null);
              }} />)}
                  </div>}
              </div>;
        })}
        </div>

        {/* Classes 11–12 */}
        <div style={{
        padding: "8px 16px 16px"
      }}>
          <SideLabel><School className="inline w-[1em] h-[1em] align-[-0.125em]" /> Classes 11 – 12</SideLabel>
          {["11", "12"].map(cls => <div key={cls}>
              <SideBtn label={`Class ${cls}`} badge={cls} active={selectedClass === cls} onClick={() => handleClassChange(cls)} />
              {selectedClass === cls && <div style={{
            paddingLeft: 40,
            paddingBottom: 8
          }}>
                  {Object.entries(CLASSES[cls].groups).map(([grp, secs]) => <div key={grp} style={{
              marginBottom: 8
            }}>
                      <div onClick={() => {
                setSelectedGroup(grp);
                setSelectedSection(secs[0]);
                setAttendance({});
                setSaveMessage(null);
              }} style={{
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: 6,
                color: selectedGroup === grp ? "#0D47A1" : "#64748b",
                borderLeft: selectedGroup === grp ? "3px solid #0D47A1" : "3px solid transparent",
                paddingLeft: 8,
                transition: "all 0.15s"
              }}>{grp}</div>
                      {selectedGroup === grp && <div style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap"
              }}>
                          {secs.map(sec => <SecBtn key={sec} sec={sec} active={selectedSection === sec} onClick={() => {
                  setSelectedSection(sec);
                  setAttendance({});
                  setSaveMessage(null);
                }} />)}
                        </div>}
                    </div>)}
                </div>}
            </div>)}
        </div>
      </div>

      {/* ════════ MAIN CONTENT ════════ */}
      <div className="lb-main" style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      minWidth: 0,
      position: "relative"
    }}>

        {/* ── Top Header ── */}
        <div className="lb-topheader" style={{
        background: "#FFFFFF",
        borderBottom: "1px solid #90CAF9",
        padding: "0 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        height: 64,
        flexShrink: 0,
        flexWrap: "wrap"
      }}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{
          background: "#E3F2FD",
          border: "none",
          borderRadius: 10,
          width: 36,
          height: 36,
          cursor: "pointer",
          color: "#0D47A1",
          fontSize: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "all 0.2s"
        }}><Menu className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>

          <div style={{
          flex: 1,
          minWidth: 0
        }}>
            <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap"
          }}>
              <span className="lb-title-text" style={{
              fontSize: 16,
              fontWeight: 700,
              color: "#0D47A1",
              whiteSpace: "nowrap"
            }}>
                Attendance — Class {selectedClass}
                {is11or12 && selectedGroup ? ` · ${selectedGroup}` : ""}
                -{selectedSection}
              </span>
              <span className="lb-date-text" style={{
              fontSize: 12,
              color: "#2196F3",
              whiteSpace: "nowrap"
            }}><Calendar className="inline w-[1em] h-[1em] align-[-0.125em]" /> {todayDisplay}</span>
            </div>
          </div>

          {/* Timetable badge - hide on mobile */}
          <div className="lb-badges" style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap"
        }}>
            {timetableLoading && <Badge bg="#E3F2FD" border="#90CAF9" color="#0D47A1">⏳ Timetable…</Badge>}
            {!timetableLoading && timetableError && <Badge bg="#E3F2FD" border="#90CAF9" color="#D32F2F" title={timetableError}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> TT Error</Badge>}
            {!timetableLoading && !timetableError && timetableData && <Badge bg="#E3F2FD" border="#90CAF9" color="#0D47A1"><Check className="inline w-[1em] h-[1em] align-[-0.125em]" /> Timetable loaded</Badge>}
            {!timetableLoading && !timetableError && !timetableData && <Badge bg="#E3F2FD" border="#90CAF9" color="#64748b"><ClipboardList className="inline w-[1em] h-[1em] align-[-0.125em]" /> No timetable</Badge>}
            {!studentsLoading && !studentsError && <Badge bg="#E3F2FD" border="#90CAF9" color="#0D47A1">
                <Users className="inline w-[1em] h-[1em] align-[-0.125em]" /> {students.length} students
              </Badge>}
          </div>
        </div>

        {/* ── Day Selector ── */}
        <div className="lb-dayselector" style={{
        background: "#FFFFFF",
        borderBottom: "1px solid #90CAF9",
        padding: "8px 12px",
        display: "flex",
        gap: 4,
        alignItems: "center",
        flexShrink: 0,
        overflowX: "auto"
      }}>
          <span style={{
          fontSize: 11,
          fontWeight: 600,
          color: "#0D47A1",
          marginRight: 4,
          whiteSpace: "nowrap"
        }}>DAY:</span>
          {DAYS.map(day => {
          const isToday = day === todayName;
          const isSelected = day === selectedDay;
          const hasTT = timetableData && timetableData[day] && Object.keys(timetableData[day]).length > 0;
          return <button key={day} onClick={() => {
            setSelectedDay(day);
            setActivePeriodId(1);
            setSaveMessage(null);
          }} style={{
            padding: "4px 10px",
            borderRadius: 40,
            fontSize: 11,
            fontWeight: isSelected ? 600 : 400,
            border: "none",
            cursor: "pointer",
            background: isSelected ? "#0D47A1" : isToday ? "#E3F2FD" : "#F5F9FF",
            color: isSelected ? "#FFFFFF" : isToday ? "#0D47A1" : "#0D47A1",
            outline: isToday && !isSelected ? "1px solid #90CAF9" : "none",
            boxShadow: isSelected ? "0 2px 8px rgba(13,71,161,0.3)" : "none",
            transition: "all 0.15s",
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
            gap: 4
          }}>
                {day.slice(0, 3)}
                {isToday && <span style={{
              fontSize: 7,
              fontWeight: 500,
              color: isSelected ? "#90CAF9" : "#2196F3"
            }}>●</span>}
                {hasTT && <span style={{
              fontSize: 6,
              color: isSelected ? "#90CAF9" : "#2196F3"
            }}>●</span>}
              </button>;
        })}
        </div>

        {/* ── Period Tabs ── */}
        <div className="lb-periodtabs" style={{
        background: "#FFFFFF",
        borderBottom: "1px solid #90CAF9",
        overflowX: "auto",
        flexShrink: 0
      }}>
          <div style={{
          display: "flex",
          padding: "0 8px",
          minWidth: "max-content",
          alignItems: "stretch"
        }}>
            {classPeriods.map(p => {
            if (p.isBreak) {
              return <div key={p.id} style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "6px 8px",
                minWidth: 60,
                borderBottom: "2px solid transparent",
                borderLeft: "1px solid #E3F2FD",
                borderRight: "1px solid #E3F2FD"
              }}>
                    <div style={{
                  fontSize: 9,
                  fontWeight: 600,
                  color: "#90CAF9",
                  letterSpacing: "0.5px"
                }}>{p.label}</div>
                    <div style={{
                  fontSize: 7,
                  marginTop: 1,
                  color: "#90CAF9"
                }}>{p.time}</div>
                  </div>;
            }
            const isActive = activePeriodId === p.id;
            const cell = getCell(selectedDay, p.id);
            const periodMarked = students.filter(s => attendance[p.id]?.[s.id]).length;
            return <button key={p.id} onClick={() => setActivePeriodId(p.id)} style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "6px 10px",
              minWidth: 80,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderBottom: isActive ? "2px solid #0D47A1" : "2px solid transparent",
              color: isActive ? "#0D47A1" : "#64748b",
              transition: "all 0.15s"
            }}>
                  <div style={{
                fontSize: 11,
                fontWeight: isActive ? 600 : 500
              }}>{p.label}</div>
                  <div style={{
                fontSize: 8,
                marginTop: 1,
                color: isActive ? "#0D47A1" : "#90CAF9"
              }}>{p.time}</div>
                  {cell?.subject ? <div style={{
                marginTop: 2,
                fontSize: 8,
                fontWeight: 600,
                background: isActive ? "#E3F2FD" : "#F5F9FF",
                color: "#0D47A1",
                borderRadius: 20,
                padding: "1px 8px",
                maxWidth: 80,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>{cell.subject}</div> : <div style={{
                marginTop: 2,
                fontSize: 8,
                color: "#90CAF9"
              }}>—</div>}
                  {periodMarked > 0 && <span style={{
                marginTop: 2,
                fontSize: 5,
                color: "#2196F3"
              }}>●</span>}
                </button>;
          })}
          </div>
        </div>

        {/* ── Period Info Bar ── */}
        <div className="lb-infobar" style={{
        background: "#E3F2FD",
        borderBottom: "1px solid #90CAF9",
        padding: "10px 16px",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        flexWrap: "wrap",
        flexShrink: 0
      }}>
          <div style={{
          minWidth: 100
        }}>
            <div style={{
            fontSize: 9,
            color: "#0D47A1",
            marginBottom: 2
          }}><Clock className="inline w-[1em] h-[1em] align-[-0.125em]" /> Period</div>
            <div style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0D47A1"
          }}>{currentPeriodMeta?.label}</div>
            <div style={{
            fontSize: 10,
            color: "#0D47A1"
          }}>{currentPeriodMeta?.time} · {currentPeriodMeta?.durationMin} min</div>
          </div>

          {currentCell?.subject ? <div className="lb-cellbox" style={{
          background: "#FFFFFF",
          border: "1px solid #90CAF9",
          borderRadius: 12,
          padding: "8px 14px",
          display: "flex",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
          boxShadow: "0 1px 2px rgba(13,71,161,0.04)",
          flex: 1
        }}>
              <InfoChip label="Subject" value={currentCell.subject} color="#0D47A1" bold />
              <VDiv />
              <InfoChip label="Teacher" value={currentCell.teacher || "Not assigned"} color="#0D47A1" />
              <VDiv />
              <InfoChip label="Duration" value={`${currentPeriodMeta?.durationMin} min`} color="#0D47A1" />
              <VDiv />
              <InfoChip label="Day" value={selectedDay} color="#0D47A1" />
            </div> : <div className="lb-cellbox" style={{
          background: "#FFFFFF",
          border: "1px dashed #90CAF9",
          borderRadius: 12,
          padding: "8px 14px",
          display: "flex",
          alignItems: "center",
          flex: 1
        }}>
              <span style={{
            color: "#0D47A1",
            marginRight: 6
          }}>ℹ</span>
              <span style={{
            fontSize: 11,
            color: "#0D47A1",
            fontStyle: "italic"
          }}>
                {timetableLoading ? "Loading timetable…" : timetableData ? `No subject assigned — ${selectedDay} · ${currentPeriodMeta?.label}` : "No timetable found for this class"}
              </span>
            </div>}

          <div style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          flexWrap: "wrap",
          marginLeft: "auto"
        }}>
            <div style={{
            display: "flex",
            gap: 4
          }}>
              <StatBadge bg="#E3F2FD" text="#0D47A1" border="#90CAF9" sym="" label="P" count={presentCount} />
              <StatBadge bg="#E3F2FD" text="#0D47A1" border="#90CAF9" sym="" label="A" count={absentCount} />
              <StatBadge bg="#E3F2FD" text="#0D47A1" border="#90CAF9" sym="⏱" label="L" count={lateCount} />
            </div>
          </div>
        </div>

        {/* ── Student List ── */}
        <div className="lb-studentlist" style={{
        flex: 1,
        overflowY: "auto",
        padding: "12px 16px 80px"
      }}>

          {/* Column headers - hide on small screens */}
          <div className="lb-grid lb-colheader" style={{
          display: "grid",
          gridTemplateColumns: "30px 40px 1fr 1fr 140px",
          padding: "0 8px 6px",
          marginBottom: 6,
          borderBottom: "1px solid #90CAF9",
          gap: 4
        }}>
            {["#", "Roll", "STUDENT", "PARENT", "ATT."].map(h => <div key={h} style={{
            ...colHead,
            fontSize: 9
          }}>{h}</div>)}
          </div>

          {/* Waiting for group selection (11/12 only) */}
          {is11or12 && !selectedGroup && <div style={{
          textAlign: "center",
          padding: "32px 0",
          color: "#0D47A1",
          fontSize: 12
        }}>
              <MousePointer2 className="inline w-[1em] h-[1em] align-[-0.125em]" /> Select a group from the sidebar to load students
            </div>}

          {/* Loading */}
          {studentsLoading && <div style={{
          textAlign: "center",
          padding: "32px 0",
          color: "#0D47A1",
          fontSize: 12
        }}>
              ⏳ Loading students from database…
            </div>}

          {/* Error */}
          {!studentsLoading && studentsError && <div style={{
          textAlign: "center",
          padding: "32px 0",
          color: "#D32F2F",
          fontSize: 12
        }}>
              <AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> Could not load students: {studentsError}
            </div>}

          {/* Empty — only show if we actually tried to fetch */}
          {!studentsLoading && !studentsError && students.length === 0 && (!is11or12 || selectedGroup) && <div style={{
          textAlign: "center",
          padding: "32px 0",
          color: "#0D47A1",
          fontSize: 12
        }}>
              <GraduationCap className="inline w-[1em] h-[1em] align-[-0.125em]" /> No admitted students found for Class {selectedClass}
              {is11or12 && selectedGroup ? ` · ${selectedGroup}` : ""}-{selectedSection}
              <div style={{
            fontSize: 10,
            marginTop: 6,
            color: "#0D47A1"
          }}>
                Make sure students are marked as "Admitted" in the Admission page
              </div>
            </div>}

          {/* Student rows */}
          {!studentsLoading && !studentsError && students.map((s, idx) => {
          const status = getStatus(s.id);
          return <div key={s.id} className="lb-grid lb-studentrow" style={{
            display: "grid",
            gridTemplateColumns: "30px 40px 1fr 1fr 140px",
            alignItems: "center",
            gap: 4,
            background: status === "P" ? "#E3F2FD" : status === "A" ? "#E3F2FD" : status === "L" ? "#E3F2FD" : "#FFFFFF",
            border: status === "P" ? "1px solid #90CAF9" : status === "A" ? "1px solid #90CAF9" : status === "L" ? "1px solid #90CAF9" : "1px solid #90CAF9",
            borderRadius: 8,
            padding: "6px 8px",
            marginBottom: 6,
            transition: "all 0.15s",
            boxShadow: "0 1px 2px rgba(13,71,161,0.04)"
          }}>
                <div style={{
              color: "#0D47A1",
              fontSize: 11,
              fontWeight: 500
            }}>{idx + 1}</div>
                <div style={{
              fontSize: 10,
              fontWeight: 600,
              color: "#0D47A1",
              background: "#E3F2FD",
              borderRadius: 4,
              padding: "1px 6px",
              textAlign: "center",
              width: "fit-content"
            }}>{s.rollNo}</div>
                <div className="lb-studentname" style={{
              fontSize: 12,
              fontWeight: 500,
              color: "#0D47A1",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}>{s.name}</div>
                <div className="lb-parentcell" style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}>
                  <div style={{
                fontSize: 11,
                color: "#0D47A1"
              }}>{s.parent}</div>
                  {s.mobile && <div style={{
                fontSize: 9,
                color: "#0D47A1"
              }}><Phone className="inline w-[1em] h-[1em] align-[-0.125em]" /> {s.mobile}</div>}
                </div>
                <div className="lb-attbtns" style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end"
            }}>
                  {["P", "A", "L"].map(st => <button key={st} className="lb-attbtn" onClick={() => mark(s.id, st)} style={{
                width: 36,
                height: 28,
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 10,
                transition: "all 0.15s",
                background: status === st ? st === "P" ? "#0D47A1" : st === "A" ? "#0D47A1" : "#0D47A1" : "#E3F2FD",
                color: status === st ? "#FFFFFF" : "#0D47A1",
                boxShadow: status === st ? "0 2px 6px rgba(13,71,161,0.3)" : "none"
              }}>{st === "L" ? "Late" : st}</button>)}
                </div>
              </div>;
        })}
        </div>

        {/* ── Bottom Bar ── */}
        <div className="lb-bottombar" style={{
        position: "sticky",
        bottom: 0,
        background: "#FFFFFF",
        borderTop: "1px solid #90CAF9",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        boxShadow: "0 -2px 10px rgba(13,71,161,0.04)",
        flexWrap: "wrap",
        zIndex: 5
      }}>
          <div style={{
          fontSize: 11,
          color: "#0D47A1",
          whiteSpace: "nowrap"
        }}>
            <Check className="inline w-[1em] h-[1em] align-[-0.125em]" /> {markedCount} of {students.length} marked
          </div>
          <div style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minWidth: 80
        }}>
            <div style={{
            fontSize: 9,
            color: "#0D47A1"
          }}>Period completion</div>
            <div style={{
            height: 4,
            borderRadius: 2,
            background: "#E3F2FD",
            overflow: "hidden"
          }}>
              <div style={{
              height: "100%",
              width: `${completion}%`,
              background: completion === 100 ? "linear-gradient(90deg,#0D47A1,#2196F3)" : "linear-gradient(90deg,#0D47A1,#2196F3)",
              borderRadius: 2,
              transition: "width 0.3s ease"
            }} />
            </div>
          </div>

          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap"
        }}>
            <div style={{
            display: "flex",
            gap: 4
          }}>
              <QuickBtn onClick={() => markAll("P")}><Check className="inline w-[1em] h-[1em] align-[-0.125em]" /> All P</QuickBtn>
              <QuickBtn onClick={() => markAll("A")}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /> All A</QuickBtn>
            </div>
            {saveMessage && <div style={{
            fontSize: 10,
            color: saveMessageType === "success" ? "#0D47A1" : "#D32F2F",
            background: saveMessageType === "success" ? "#E3F2FD" : "#E3F2FD",
            padding: "4px 10px",
            borderRadius: 40,
            whiteSpace: "nowrap",
            border: "1px solid #90CAF9"
          }}>
                {saveMessage}
              </div>}
            <button onClick={handleSave} disabled={saving || students.length === 0 || currentPeriodMeta?.isBreak} style={{
            background: saving || students.length === 0 || currentPeriodMeta?.isBreak ? "#90CAF9" : "linear-gradient(135deg,#0D47A1,#2196F3)",
            border: "none",
            borderRadius: 40,
            padding: "8px 20px",
            color: saving || students.length === 0 || currentPeriodMeta?.isBreak ? "#FFFFFF" : "#FFFFFF",
            fontWeight: 600,
            fontSize: 12,
            cursor: saving || students.length === 0 || currentPeriodMeta?.isBreak ? "not-allowed" : "pointer",
            boxShadow: saving || students.length === 0 || currentPeriodMeta?.isBreak ? "none" : "0 4px 12px rgba(13,71,161,0.3)",
            whiteSpace: "nowrap",
            fontFamily: "'Poppins', sans-serif",
            transition: "all 0.15s"
          }}>
              <Save className="inline w-[1em] h-[1em] align-[-0.125em]" /> {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      {/* Fonts */}
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: #E3F2FD; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: #90CAF9; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: #2196F3; }

        /* Backdrop only shows on mobile when sidebar is open */
        .lb-backdrop { display: none; }

        /* ═══════════════════════════════════════════════════════════
           MOBILE VIEW ONLY — everything below this line is scoped to
           max-width:768px. Desktop layout/logic is completely untouched.
           ═══════════════════════════════════════════════════════════ */
        @media (max-width: 768px) {

          /* Sidebar becomes a slide-in drawer instead of pushing content */
          .lb-sidebar {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            bottom: 0 !important;
            height: 100vh !important;
            width: 82vw !important;
            max-width: 300px !important;
            min-width: 0 !important;
            transform: translateX(-100%);
            transition: transform 0.25s ease !important;
            z-index: 200 !important;
            box-shadow: 4px 0 24px rgba(13,71,161,0.18) !important;
          }
          .lb-sidebar.open {
            transform: translateX(0);
          }

          .lb-backdrop.show {
            display: block;
            position: fixed;
            inset: 0;
            background: rgba(13, 71, 161, 0.35);
            z-index: 150;
          }

          .lb-main { width: 100vw; }

          /* Top header: stack title + date, shrink badges */
          .lb-topheader {
            height: auto !important;
            padding: 8px 10px !important;
            gap: 8px !important;
          }
          .lb-title-text { font-size: 13px !important; white-space: normal !important; }
          .lb-date-text { font-size: 10px !important; }
          .lb-badges { gap: 4px !important; }
          .lb-badges > div { font-size: 8px !important; padding: 2px 6px !important; }

          .lb-dayselector { padding: 6px 8px !important; }
          .lb-periodtabs button { min-width: 64px !important; padding: 5px 6px !important; }

          .lb-infobar { padding: 8px 10px !important; gap: 8px !important; }
          .lb-cellbox { padding: 6px 10px !important; gap: 8px !important; width: 100%; }

          /* Grid: shrink columns so ATT buttons fit without overflow */
          .lb-grid {
            grid-template-columns: 18px 30px 1fr 78px !important;
            gap: 3px !important;
          }
          .lb-colheader div:nth-child(4) { display: none; }
          .lb-parentcell { display: none !important; }

          .lb-studentname { font-size: 11px !important; }
          .lb-studentrow { padding: 6px 6px !important; }

          .lb-attbtns { gap: 3px !important; }
          .lb-attbtn {
            width: 24px !important;
            height: 26px !important;
            font-size: 8px !important;
            border-radius: 5px !important;
            padding: 0 !important;
          }

          .lb-studentlist { padding: 8px 8px 90px !important; }

          .lb-bottombar {
            padding: 8px 10px !important;
            gap: 8px !important;
          }
          .lb-bottombar > div:first-child { font-size: 9px !important; }
        }

        /* Extra-small phones */
        @media (max-width: 380px) {
          .lb-grid {
            grid-template-columns: 16px 26px 1fr 70px !important;
          }
          .lb-attbtn {
            width: 21px !important;
            height: 24px !important;
            font-size: 7px !important;
          }
        }
      `}</style>
    </div>;
}

// ─── Micro-components ─────────────────────────────────────────────────────────

function SideLabel({
  children
}) {
  return <div style={{
    fontSize: 10,
    fontWeight: 600,
    color: "#2196F3",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    marginBottom: 8,
    paddingLeft: 4
  }}>{children}</div>;
}
function SideBtn({
  label,
  badge,
  active,
  onClick
}) {
  return <button onClick={onClick} style={{
    width: "100%",
    background: active ? "#E3F2FD" : "transparent",
    border: "none",
    borderRadius: 8,
    padding: "6px 10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    color: active ? "#0D47A1" : "#0D47A1",
    fontSize: 12,
    fontWeight: active ? 600 : 500,
    textAlign: "left",
    transition: "all 0.15s",
    marginBottom: 2
  }}>
      <span style={{
      width: 22,
      height: 22,
      borderRadius: 6,
      background: active ? "#90CAF9" : "#E3F2FD",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 10,
      fontWeight: 700,
      color: active ? "#0D47A1" : "#0D47A1",
      flexShrink: 0
    }}>{badge}</span>
      {label}
    </button>;
}
function SecBtn({
  sec,
  active,
  onClick
}) {
  return <button onClick={onClick} style={{
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    background: active ? "#0D47A1" : "#E3F2FD",
    color: active ? "#FFFFFF" : "#0D47A1",
    transition: "all 0.15s"
  }}>{sec}</button>;
}
function Badge({
  bg,
  border,
  color,
  children,
  title
}) {
  return <div title={title} style={{
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 40,
    padding: "2px 10px",
    fontSize: 10,
    color,
    whiteSpace: "nowrap",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 4
  }}>{children}</div>;
}
function InfoChip({
  label,
  value,
  color,
  bold
}) {
  return <div>
      <div style={{
      fontSize: 8,
      color: "#0D47A1",
      marginBottom: 1,
      textTransform: "uppercase",
      letterSpacing: "0.5px"
    }}>{label}</div>
      <div style={{
      fontSize: 12,
      fontWeight: bold ? 700 : 600,
      color
    }}>{value}</div>
    </div>;
}
function VDiv() {
  return <div style={{
    width: 1,
    height: 28,
    background: "#90CAF9",
    flexShrink: 0
  }} />;
}
function StatBadge({
  bg,
  text,
  border,
  sym,
  label,
  count
}) {
  return <span style={{
    background: bg,
    border: `1px solid ${border}`,
    color: text,
    borderRadius: 20,
    padding: "2px 8px",
    fontSize: 10,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 2
  }}>{sym} {count}</span>;
}
function QuickBtn({
  onClick,
  children
}) {
  return <button onClick={onClick} style={{
    background: "#E3F2FD",
    border: "1px solid #90CAF9",
    borderRadius: 20,
    padding: "4px 12px",
    fontSize: 10,
    color: "#0D47A1",
    cursor: "pointer",
    fontFamily: "'Poppins', sans-serif",
    transition: "all 0.15s",
    display: "inline-flex",
    alignItems: "center",
    gap: 4
  }}>{children}</button>;
}
const colHead = {
  fontSize: 9,
  fontWeight: 600,
  color: "#0D47A1",
  letterSpacing: "0.5px",
  textTransform: "uppercase"
};